import React, { useEffect, useState } from 'react';
import Breadcrumb from '../../../components/Breadcrumb';
import { Link, useNavigate } from 'react-router-dom';
import { createProjectTransaction } from '../../../axios/ProjectTransactionAxios';
import Swal from 'sweetalert2';
import TableTransaction from '../../../components/TableTransaction';
import LoadingOverlay from '../../../components/LoadingOverlay';

function ProjectTransactionAdd() {
  const breadcrumbItems = [
    { label: 'Dashboard', href: '/' },
    { label: 'Project Transaction', href: '/project-transaction' },
    { label: 'Create Project Transaction' },
  ];

  const navigation = useNavigate();
  const [formData, setFormData] = useState({
    rtanggal: '',
    rreferensi: '',
    rterang: '',
    rjumlah: '',
    journals: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const forms = document.querySelectorAll('.needs-validation');
    Array.from(forms).forEach((form) => {
      form.addEventListener(
        'submit',
        (event) => {
          if (!form.checkValidity()) {
            event.preventDefault();
            event.stopPropagation();
          }
          form.classList.add('was-validated');
        },
        false
      );
    });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Jika input adalah rjumlah, format angka
    if (name === 'rjumlah') {
      // Menghapus semua karakter non-digit
      const numericValue = value.replace(/[^0-9]/g, '');
      // Menghapus nol di depan
      const trimmedValue = numericValue.replace(/^0+/, '');
      // Format dengan pemisah ribuan
      const formattedValue = trimmedValue ? formatNumber(trimmedValue) : '';
      setFormData({
        ...formData,
        [name]: formattedValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const formatNumber = (num) => {
    return num.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const formElements = Array.from(e.target.form.elements);
      const index = formElements.indexOf(e.target);
      formElements[index + 1]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    if (!form.checkValidity()) {
      form.classList.add('was-validated');
      return;
    }

    // Mengirim nilai rjumlah tanpa pemisah ribuan
    const submittedData = {
      ...formData,
      rjumlah: formData.rjumlah.replace(/,/g, ''), // Menghapus pemisah ribuan
    };

    // Calculate total debit and credit
    const totalDebet = formData.journals.reduce(
      (sum, journal) => sum + parseFloat(journal.rdebet || 0),
      0
    );
    const totalKredit = formData.journals.reduce(
      (sum, journal) => sum + parseFloat(journal.rkredit || 0),
      0
    );

    // Check if totals are equal
    if (totalDebet !== totalKredit) {
      Swal.fire({
        title: 'Error!',
        text: 'Total debit and credit amounts must be equal.',
        icon: 'error',
      });
      return;
    }

    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-primary mx-1',
        denyButton: 'btn btn-danger mx-1',
        cancelButton: 'btn btn-secondary mx-1',
      },
      buttonsStyling: false,
    });

    try {
      const result = await swalWithBootstrapButtons.fire({
        title: 'Do you want to save the data?',
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: 'Save',
        cancelButtonText: `Don't save`,
      });

      if (result.isConfirmed) {
        setLoading(true);
        try {
          await createProjectTransaction(submittedData);
          swalWithBootstrapButtons.fire(
            'Saved!',
            'The transaction has been created.',
            'success'
          );
          navigation('/project-transaction');
        } catch (createTransactionError) {
          swalWithBootstrapButtons.fire({
            title: 'Error!',
            text: createTransactionError.message,
            icon: 'error',
          });
        } finally {
          setLoading(false);
        }
      } else if (result.isDenied) {
        swalWithBootstrapButtons.fire('Changes are not saved', '', 'info');
      }
    } catch (error) {
      swalWithBootstrapButtons.fire({
        title: 'Error!',
        text: 'There was an error creating the transaction.',
        icon: 'error',
      });
      console.log(error);
    }
  };

  const handleCancel = (event) => {
    event.preventDefault();

    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-primary mx-1',
        denyButton: 'btn btn-danger mx-1',
        cancelButton: 'btn btn-secondary mx-1',
      },
      buttonsStyling: false,
    });
    swalWithBootstrapButtons
      .fire({
        title: 'Are you sure?',
        text: 'All your input will be lost!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, cancel it!',
        cancelButtonText: 'No, keep it',
      })
      .then((result) => {
        if (result.isConfirmed) {
          const form = document.querySelector('.needs-validation');
          form.reset();
          setFormData({
            rtanggal: '',
            rreferensi: '',
            rterang: '',
            rjumlah: '',
            journals: [],
          });
          form.classList.remove('was-validated');
        }
      });
  };

  const handleTableChange = (updatedItems) => {
    setFormData({
      ...formData,
      journals: updatedItems,
    });
  };

  const columnMap = {
    rkoderek: 'Account Code',
    rdebet: 'Debet',
    rkredit: 'Credit',
    rreftgl: 'Due Date',
    rrefterang: 'Note',
    bunit: 'Business Unit',
    rkodepro: 'Project',
    rkodeext: 'External',
    rkodeint: 'Internal',
    rkodeinv: 'Invoice',
    rkodepo: 'Purchase Order',
  };

  const columnTypes = {
    rreftgl: 'date', // Menentukan tipe input date untuk rreftgl
    // Tipe lainnya default ke 'text'
  };

  return (
    <div>
      <LoadingOverlay show={loading} message="Saving transaction data..." />
      <Breadcrumb items={breadcrumbItems}></Breadcrumb>
      <form
        className="needs-validation"
        noValidate
        autocomplete="off"
        onSubmit={handleSubmit}
      >
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-body">
                <div className="row mb-3">
                  <label className="col-sm-2 col-form-label">Date</label>
                  <div className="col-sm-4">
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      name="rtanggal"
                      value={formData.rtanggal}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      required
                    />
                    <div className="invalid-feedback">Cannot be empty.</div>
                  </div>
                </div>
                <div className="row mb-3">
                  <label className="col-sm-2 col-form-label">Reference</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="rreferensi"
                      value={formData.rreferensi}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      required
                    />
                    <div className="invalid-feedback">Cannot be empty.</div>
                  </div>
                </div>
                <div className="row mb-3">
                  <label className="col-sm-2 col-form-label">Description</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="rterang"
                      value={formData.rterang}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      required
                    />
                    <div className="invalid-feedback">Cannot be empty.</div>
                  </div>
                </div>
                <div className="row mb-3">
                  <label className="col-sm-2 col-form-label">Amount</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className="form-control form-control-sm right-align"
                      name="rjumlah"
                      value={formData.rjumlah}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      required
                      min={0}
                    />
                    <div className="invalid-feedback">Cannot be empty.</div>
                  </div>
                </div>
                <div className="row mb-3">
                  <label className="col-sm-2 col-form-label">Status</label>
                  <div className="col-sm-4">
                    <input
                      type="text"
                      className="form-control"
                      value={'M'}
                      disabled
                    />
                  </div>
                </div>
                <TableTransaction
                  items={formData.journals}
                  columnMap={columnMap}
                  columnTypes={columnTypes}
                  onAddRow={(updatedItems) => handleTableChange(updatedItems)}
                  onDeleteRow={(updatedItems) =>
                    handleTableChange(updatedItems)
                  }
                  onChange={handleTableChange}
                  journalTgl={formData.rtanggal}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-end">
          <button type="submit" className="btn btn-primary mx-2">
            <i className="bi bi-send"></i>
            <span> Submit</span>
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleCancel}
          >
            <i className="bi bi-x-circle"></i>
            <span> Cancel</span>
          </button>
          <Link
            type="button"
            className="btn btn-warning mx-2"
            to="/project-transaction"
          >
            <i className="bi bi-arrow-left-circle"></i>
            <span> Back</span>
          </Link>
        </div>
      </form>
    </div>
  );
}

export default ProjectTransactionAdd;
